import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Building, Plus, Pencil, Trash2, Search, Globe, MapPin, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function MallGroupsPage() {
  const { api, isSuper } = useAuth();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    headquarters: '',
    website: '',
    logo_url: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    try {
      const response = await api.get('/groups');
      setGroups(response.data);
    } catch (error) {
      console.error('Failed to fetch groups:', error);
      toast.error('Failed to load mall groups');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingGroup) {
        await api.put(`/groups/${editingGroup.id}`, formData);
        toast.success('Mall group updated successfully');
      } else {
        await api.post('/groups', formData);
        toast.success('Mall group created successfully');
      }
      setDialogOpen(false);
      fetchGroups();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save mall group');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (group) => {
    setEditingGroup(group);
    setFormData({
      name: group.name,
      description: group.description || '',
      headquarters: group.headquarters || '',
      website: group.website || '',
      logo_url: group.logo_url || '',
    });
    setDialogOpen(true);
  };

  const handleDelete = async (groupId) => {
    if (!confirm('Are you sure you want to delete this mall group?')) return;

    try {
      await api.delete(`/groups/${groupId}`);
      toast.success('Mall group deleted successfully');
      fetchGroups();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete mall group');
    }
  };

  const openNewDialog = () => {
    setEditingGroup(null);
    setFormData({
      name: '',
      description: '',
      headquarters: '',
      website: '',
      logo_url: '',
    });
    setDialogOpen(true);
  };

  const filteredGroups = groups.filter((group) =>
    group.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    group.headquarters?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-10 h-10 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="mall-groups-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold">Mall Groups</h2>
          <p className="text-muted-foreground">Manage mall group organizations</p>
        </div>
        {isSuper && (
          <Button className="btn-primary" onClick={openNewDialog} data-testid="add-group-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Group
          </Button>
        )}
      </div>

      {/* Search */}
      <div className="search-input-wrapper max-w-md">
        <Search className="search-icon w-4 h-4" />
        <Input
          placeholder="Search groups..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="input-dark pl-10"
          data-testid="search-groups"
        />
      </div>

      {/* Groups Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredGroups.map((group, index) => (
          <Card
            key={group.id}
            className="glass-card hover:border-primary/30 transition-all duration-300 animate-fade-in"
            style={{ animationDelay: `${index * 0.05}s` }}
            data-testid={`group-card-${group.id}`}
          >
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Building className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{group.name}</h3>
                    {group.headquarters && (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                        <MapPin className="w-3 h-3" />
                        {group.headquarters}
                      </div>
                    )}
                  </div>
                </div>
                <Badge variant="secondary">{group.mall_count} malls</Badge>
              </div>

              {group.description && (
                <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                  {group.description}
                </p>
              )}

              {group.website && (
                <a
                  href={group.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-xs text-primary hover:underline mb-4"
                >
                  <Globe className="w-3 h-3" />
                  {group.website.replace(/^https?:\/\//, '')}
                </a>
              )}

              {isSuper && (
                <div className="flex gap-2 pt-4 border-t border-border/40">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => handleEdit(group)}
                    data-testid={`edit-group-${group.id}`}
                  >
                    <Pencil className="w-3 h-3 mr-1" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    onClick={() => handleDelete(group.id)}
                    data-testid={`delete-group-${group.id}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredGroups.length === 0 && (
        <div className="text-center py-12">
          <Building className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No mall groups found</p>
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="glass-panel sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingGroup ? 'Edit Mall Group' : 'Add Mall Group'}</DialogTitle>
            <DialogDescription>
              {editingGroup ? 'Update the mall group details' : 'Create a new mall group organization'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Group Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="input-dark"
                  required
                  data-testid="group-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Input
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="input-dark"
                  data-testid="group-description-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="headquarters">Headquarters</Label>
                <Input
                  id="headquarters"
                  value={formData.headquarters}
                  onChange={(e) => setFormData({ ...formData, headquarters: e.target.value })}
                  className="input-dark"
                  placeholder="e.g., Mumbai"
                  data-testid="group-headquarters-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="website">Website</Label>
                <Input
                  id="website"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  className="input-dark"
                  placeholder="https://example.com"
                  data-testid="group-website-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-primary" disabled={saving} data-testid="save-group-btn">
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
